<script setup>
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@chatwoot/pico-search';
import MacrosTableRow from './MacrosTableRow.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import { BaseTable } from 'dashboard/components-next/table';
import { useAdmin } from 'dashboard/composables/useAdmin';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeleteConfirmationPopup = ref(false);
const selectedMacro = ref({});
const searchQuery = ref('');
// CUSTOM (Portela 2026-09-09): modal de "sem permissão" ao tentar criar/editar/excluir.
const showPermissionModal = ref(false);

const records = computed(() => getters['macros/getMacros'].value);
const uiFlags = computed(() => getters['macros/getUIFlags'].value);

// CUSTOM (Portela 2026-09-09): permissão POR AGENTE p/ GERENCIAR macros. Admin sempre;
// agente só com macros_enabled (getCurrentUser.accounts[].macros_enabled). Quem NÃO pode
// ainda VÊ e consulta a lista (precisa da palavra-chave p/ usar), mas adicionar/editar/
// excluir abre um modal p/ contatar o admin.
const currentUser = computed(() => getters.getCurrentUser.value);
const currentAccountId = computed(() => getters.getCurrentAccountId.value);
const canManageMacros = computed(() => {
  if (isAdmin.value) return true;
  const account = (currentUser.value?.accounts || []).find(
    acc => acc.id === currentAccountId.value
  );
  return Boolean(account?.macros_enabled);
});

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['name']);
});

const deleteMessage = computed(() => ` ${selectedMacro.value.name}?`);

onMounted(() => {
  store.dispatch('macros/get');
});

const openPermissionModal = () => {
  showPermissionModal.value = true;
};

const onAddMacro = () => {
  if (!canManageMacros.value) {
    openPermissionModal();
    return;
  }
  router.push({ name: 'macros_new' });
};

const deleteMacro = async id => {
  try {
    await store.dispatch('macros/delete', id);
    useAlert(t('MACROS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('MACROS.DELETE.API.ERROR_MESSAGE'));
  }
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedMacro.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = () => {
  closeDeletePopup();
  deleteMacro(selectedMacro.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('MACROS.LIST.TABLE_HEADER.NAME'),
    t('MACROS.LIST.TABLE_HEADER.CREATED BY'),
    t('MACROS.LIST.TABLE_HEADER.LAST_UPDATED_BY'),
    t('MACROS.LIST.TABLE_HEADER.VISIBILITY'),
    t('MACROS.LIST.TABLE_HEADER.ACTIONS'),
  ];
});
</script>

<template>
  <SettingsLayout
    :no-records-message="$t('MACROS.LIST.404')"
    :no-records-found="!records.length"
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('MACROS.LOADING')"
    feature-name="macros"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('MACROS.HEADER')"
        :description="$t('MACROS.DESCRIPTION')"
        :link-text="$t('MACROS.LEARN_MORE')"
        :search-placeholder="$t('MACROS.SEARCH_PLACEHOLDER')"
        feature-name="macros"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('MACROS.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <!-- CUSTOM: sempre visível; se não pode gerenciar, abre modal de permissão -->
          <Button
            :label="$t('MACROS.HEADER_BTN_TXT')"
            size="sm"
            @click="onAddMacro"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredRecords"
        :no-data-message="
          searchQuery ? $t('MACROS.NO_RESULTS') : $t('MACROS.LIST.404')
        "
      >
        <template #row="{ items }">
          <MacrosTableRow
            v-for="macro in items"
            :key="macro.id"
            :macro="macro"
            :can-manage-public-macros="isAdmin"
            :blocked="!canManageMacros"
            @delete="openDeletePopup(macro)"
            @blocked="openPermissionModal"
          />
        </template>
      </BaseTable>
      <woot-delete-modal
        v-model:show="showDeleteConfirmationPopup"
        :on-close="closeDeletePopup"
        :on-confirm="confirmDeletion"
        :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
        :message="$t('MACROS.DELETE.CONFIRM.MESSAGE')"
        :message-value="deleteMessage"
        :confirm-text="$t('MACROS.DELETE.CONFIRM.YES')"
        :reject-text="$t('MACROS.DELETE.CONFIRM.NO')"
      />
      <!-- CUSTOM (Portela 2026-09-09): modal "sem permissão" p/ criar/editar/excluir -->
      <woot-modal
        v-model:show="showPermissionModal"
        :on-close="() => (showPermissionModal = false)"
      >
        <div class="flex flex-col items-center gap-3 p-8 text-center">
          <span class="i-lucide-lock-keyhole size-8 text-n-slate-10" />
          <h3 class="text-heading-2 text-n-slate-12">
            {{ $t('MACROS.RESTRICTED.TITLE') }}
          </h3>
          <p class="text-body-main text-n-slate-11 max-w-sm">
            {{ $t('MACROS.RESTRICTED.MESSAGE') }}
          </p>
          <Button
            :label="$t('MACROS.RESTRICTED.GOT_IT')"
            size="sm"
            @click="showPermissionModal = false"
          />
        </div>
      </woot-modal>
    </template>
  </SettingsLayout>
</template>
